/**
 * Environment detection utilities for image processing.
 * Determines which processing backends are available based on runtime environment.
 */

/**
 * Check if we're running in an environment where Sharp is available (Node.js or Bun).
 * Returns false in browsers and other non-server environments.
 */
export function isSharpAvailable(): boolean {
    // Node.js: has process.versions.node
    const isNodejs = typeof process === "object" && process !== null && typeof process.versions === "object" && typeof process.versions.node === "string"
    // Bun: has globalThis.Bun
    const isBun = typeof globalThis !== "undefined" && "Bun" in globalThis
    return isNodejs || isBun
}

/**
 * Feature flags for individual optimized image processing operations.
 * Each flag enables a specific optimized implementation to help identify
 * which optimization causes differences compared to production.
 *
 * All flags default to false (use legacy implementations).
 * Set to "true" to enable the optimized version of that operation.
 */
export const OptimizedOps = {
    /** Optimized LUT application with tetrahedral interpolation (no allocations per pixel) */
    applyLut: (): boolean => typeof process !== "undefined" && process.env?.CM_OPT_APPLY_LUT === "true",

    /** Use Sharp for convolve instead of TypeScript */
    convolveSharp: (): boolean => typeof process !== "undefined" && process.env?.CM_OPT_CONVOLVE_SHARP === "true",

    /** Use optimized extractChannel with typed arrays */
    extractChannel: (): boolean => typeof process !== "undefined" && process.env?.CM_OPT_EXTRACT_CHANNEL === "true",

    /** Use optimized combineChannels with typed arrays */
    combineChannels: (): boolean => typeof process !== "undefined" && process.env?.CM_OPT_COMBINE_CHANNELS === "true",

    /** Enable operation logging for all operations */
    logging: (): boolean => typeof process !== "undefined" && process.env?.CM_OPT_LOGGING === "true",
} as const
